// Pure placement resolver for background-audio (SFX/Music) beds. Given a
// bed's duration + its stored offset/sourceOffset and the asset length,
// returns where on the asset timeline the bed plays and which slice of the
// bed buffer is used. Single source of truth: the Timeline preview, the
// export renderer, and the SfxTrack drag UI all resolve through this — no
// second copy of the two-mode logic anywhere.
//
//   - assetLen >= bedLen  → 'place':  clip sits at `offset` on the timeline,
//                           silence around it, whole bed plays.
//   - assetLen <  bedLen  → 'window': asset-length window over the bed,
//                           `sourceOffset` selects where the window starts.

export interface BedPlacement {
  mode: 'place' | 'window';
  /** Seconds on the asset timeline where the bed becomes audible. */
  timelineStart: number;
  /** Seconds into the bed buffer where playback starts. */
  sourceStart: number;
  /** Seconds of audio that play. */
  playLen: number;
}

export interface BedPlacementInput {
  duration: number;
  offset?: number;
  sourceOffset?: number;
}

const snap = (x: number): number => Math.round(x * 10) / 10;
const clamp = (x: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, x));

export function resolveBedPlacement(
  bed: BedPlacementInput,
  assetLen: number,
): BedPlacement {
  const bedLen = Math.max(0, bed.duration || 0);
  if (bedLen <= 0 || assetLen <= 0) {
    return { mode: 'place', timelineStart: 0, sourceStart: 0, playLen: 0 };
  }
  if (assetLen >= bedLen) {
    const timelineStart = clamp(snap(bed.offset ?? 0), 0, assetLen - bedLen);
    return { mode: 'place', timelineStart, sourceStart: 0, playLen: bedLen };
  }
  const sourceStart = clamp(snap(bed.sourceOffset ?? 0), 0, bedLen - assetLen);
  return { mode: 'window', timelineStart: 0, sourceStart, playLen: assetLen };
}

export interface BedSchedule {
  /** AudioContext clock time to pass to src.start(). */
  when: number;
  /** Seconds into the bed buffer to start from. */
  offsetIntoBuffer: number;
  /** Seconds to play. */
  playLen: number;
}

/** Combine a resolved placement with the preview playhead position.
 *  `anchor` is the shared AudioContext-time snapshot the caller uses for
 *  every src.start() in the same schedule pass. Returns null when the bed
 *  is not audible from `playheadFrom` (already ended, or zero-length). */
export function computeBedSchedule(
  placement: BedPlacement,
  playheadFrom: number,
  anchor: number,
): BedSchedule | null {
  const { timelineStart, sourceStart, playLen } = placement;
  if (playLen <= 0) return null;
  const timelineEnd = timelineStart + playLen;
  if (playheadFrom >= timelineEnd) return null;
  if (playheadFrom <= timelineStart) {
    return {
      when: anchor + (timelineStart - playheadFrom),
      offsetIntoBuffer: sourceStart,
      playLen,
    };
  }
  const into = playheadFrom - timelineStart;
  return {
    when: anchor,
    offsetIntoBuffer: sourceStart + into,
    playLen: playLen - into,
  };
}
